import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import "./layout.css";

function KebabIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ui-overflow-menu__icon" aria-hidden>
      <circle cx="12" cy="6" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="18" r="1.6" fill="currentColor" />
    </svg>
  );
}

/**
 * Kebab (three dots) for secondary actions. Children stay mounted while the
 * panel is hidden so a confirm dialog opened from an item is not thrown away.
 */
export function OverflowMenu({
  label = "More actions",
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onPointer(event: globalThis.MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="ui-overflow-menu" ref={root}>
      <button
        type="button"
        className="ui-overflow-menu__button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label}
        onClick={() => setOpen((value) => !value)}
      >
        <KebabIcon />
      </button>
      <div
        id={menuId}
        role="menu"
        hidden={!open}
        className="ui-overflow-menu__panel"
        onClick={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest("button, a")) setOpen(false);
        }}
      >
        {children}
      </div>
    </div>
  );
}
